import React, { useEffect, useRef, useState } from "react";
import { THEME_GET, THEME_CHANGE, TEAM_BRANDING } from "../../../utils/constants/apiUrl";
import { get, put } from "../../../server";
import { loggedInUser, subscribedPackage, teamIDCheck } from "../../../utils/store";
import { useAtom } from "jotai";
import showToast from "../../../helpers/showToast";
import Loader from "../../Loader";
import { Button, Card, Form, Nav } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { trackEvent } from "../../../utils/mixpanel";
import chroma from "chroma-js";
import { applyCSSVariables, applyDarkTheme, applyLightTheme, applyBrandingColors } from "../../../utils/themeUtils";
import uploadImage from "../../../helpers/uploadImage";
import baseURL from "../../../utils/constants/baseUrl";
import { CardBackGroundImages } from "../../../utils/data/CardFunctionsData";

const Theme = () => {
  const [loggedUser, setLoggedUser] = useAtom(loggedInUser);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [purchasedPackage, setPurchasedPackage] = useAtom(subscribedPackage);
  const [themeColor, setThemeColor] = useState("");
  const [buttonColor, setButtonColor] = useState("");
  const [defaultTheme, setDefaultTheme] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState("");
  const [useCustomColor, setUseCustomColor] = useState(false);
  const [customThemeScope, setCustomThemeScope] = useState(() =>
    localStorage.getItem("customThemeScope") || "team"
  );

  // Branding assets
  const [brandingTeamId] = useAtom(teamIDCheck);
  const [teamIdFromApi, setTeamIdFromApi] = useState(null);
  const [logo, setLogo] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [cardBackgroundImage, setCardBackgroundImage] = useState("");
  const [cardBgUploading, setCardBgUploading] = useState(false);
  const [cardColor, setCardColor] = useState("");
  const logoFileRef = useRef(null);
  const cardBgFileRef = useRef(null);

  const [activeTab, setActiveTab] = useState("default");

  const navigate = useNavigate();

  const Colors = [
    {
      id: 1,
      bgColor: "#affdea",
    },
    {
      id: 2,
      bgColor: "#ffb2b2",
    },
    {
      id: 3,
      bgColor: "#FFEBBE",
    },
    {
      id: 4,
      bgColor: "#F6DDFF",
    },
    {
      id: 5,
      bgColor: "#EEB8C9",
    },
    {
      id: 6,
      bgColor: "#f7ffd6",
    },
    {
      id: 7,
      bgColor: "#C7FFC4",
    },
    {
      id: 8,
      bgColor: "#D4F2FF",
    },
    {
      id: 9,
      bgColor: "#FFCFCF",
    },
    // {
    //   id: 10,
    //   bgColor: "",
    //   custom: true,
    // },
  ];


  useEffect(() => {
    if (!useCustomColor) {
      if (selectedTheme === "dark") {
        setThemeColor("#000000");
        setButtonColor("#09A0FF");
        applyDarkTheme();
      } else if (selectedTheme === "default") {
        setThemeColor("");
        setButtonColor("");
        applyLightTheme();
      } else {
        setThemeColor("");
        setButtonColor("");
      }
    }
  }, [selectedTheme, useCustomColor]);

  useEffect(() => {
    if (useCustomColor && themeColor) {
      applyCSSVariables(themeColor);
      applyBrandingColors(buttonColor || null, cardColor || null);
    }
  }, [themeColor, useCustomColor, buttonColor, cardColor]);

  // When user switches to Team Branding tab while Dark preset is active,
  // clear the dark-preset button color marker so it doesn't bleed into Team Branding saves.
  useEffect(() => {
    if (activeTab === "team" && selectedTheme === "dark" && !useCustomColor) {
      setButtonColor("");
    }
  }, [activeTab, selectedTheme, useCustomColor]);

  const handleCardBgChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCardBgUploading(true);
    const { status, data } = await uploadImage(
      "card_background",
      file,
      loggedUser?.token || null
    );
    if (status && data) {
      setCardBackgroundImage(data);
    }
    setCardBgUploading(false);
    e.target.value = "";
  };

  const callBrandingApi = async (payload) => {
    const token = loggedUser?.token || null;
    const effectiveTeamId = teamIdFromApi || brandingTeamId || loggedUser?.teams?.id;
    if (!effectiveTeamId) return;
    const { status, message } = await put(TEAM_BRANDING(effectiveTeamId), payload, token);
    showToast(status, message);
  };

  const handleRemoveLogo = async () => {
    setLogo("");
    setLogoPreview("");
    await callBrandingApi({ logo: null });
  };

  const handleClearCardBg = async () => {
    setCardBackgroundImage("");
    await callBrandingApi({ card_background_image: null });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    setLogoUploading(true);
    const { status, data } = await uploadImage(
      "card_logo",
      file,
      loggedUser?.token || null
    );
    if (status && data) {
      setLogo(data);
    }
    setLogoUploading(false);
  };

  const handleDefaultSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = loggedUser?.token || null;
    const themePayload = {
      theme_color: selectedTheme === "default" ? null : themeColor,
      button_color: selectedTheme === "default" ? null : buttonColor,
    };
    try {
      const { status, message } = await put(THEME_CHANGE, themePayload, token);
      trackEvent("Theme Updated", { status, data: themePayload });
      showToast(status, message);
      if (status) {
        if (useCustomColor && customThemeScope === "default") {
          localStorage.setItem("customThemeScope", "default");
        } else {
          localStorage.removeItem("customThemeScope");
        }
      }
      if (status && selectedTheme === "default") {
        // Light theme: use default --blue-color for buttons, only preserve card branding
        applyLightTheme();
        applyBrandingColors(null, loggedUser?.teams?.card_color);
      } else if (status && selectedTheme === "dark") {
        applyDarkTheme();
      }
    } catch (error) {
      console.error("Theme update error:", error);
    }
    setLoading(false);
  };

  const handleTeamBrandingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = loggedUser?.token || null;
    const themePayload = {
      theme_color: themeColor || null,
      button_color: buttonColor || null,
    };
    try {
      const { status: themeStatus, message: themeMessage } = await put(THEME_CHANGE, themePayload, token);
      trackEvent("Theme Updated", { status: themeStatus, data: themePayload });
      let notificationStatus = themeStatus;
      let notificationMessage = themeMessage;
      if (themeStatus && useCustomColor && customThemeScope === "team") {
        localStorage.setItem("customThemeScope", "team");
      }
      if (themeStatus && themeColor) applyCSSVariables(themeColor);

      const effectiveTeamId = teamIdFromApi || brandingTeamId || loggedUser?.teams?.id;
      if (effectiveTeamId) {
        const brandingPayload = {
          logo: logo || undefined,
          card_logo_shape: "circle",
          card_background_image: cardBackgroundImage || undefined,
          card_color: cardColor || undefined,
        };
        const { status: brandingStatus, message: brandingMessage, data: brandingData } = await put(
          TEAM_BRANDING(effectiveTeamId),
          brandingPayload,
          token
        );
        trackEvent("Branding Updated", { status: brandingStatus, data: brandingPayload });
        notificationStatus = themeStatus && brandingStatus;
        notificationMessage = !themeStatus
          ? themeMessage
          : brandingMessage;
        if (brandingStatus) {
          const savedTeamBranding = {
            ...(loggedUser?.teams || {}),
            ...(brandingData || {}),
            id: effectiveTeamId,
            logo: brandingData?.logo || logo || loggedUser?.teams?.logo,
            card_background_image:
              brandingData?.card_background_image ||
              cardBackgroundImage ||
              loggedUser?.teams?.card_background_image,
            card_color:
              brandingData?.card_color ||
              cardColor ||
              loggedUser?.teams?.card_color,
          };
          setLoggedUser((prev) => ({
            ...prev,
            teams: savedTeamBranding,
          }));
          applyBrandingColors(
            brandingData?.button_color || buttonColor,
            brandingData?.card_color || cardColor
          );
        }
      }
      showToast(notificationStatus, notificationMessage);
    } catch (error) {
      console.error("Team branding update error:", error);
    }
    setLoading(false);
  };

  const fetchColors = async () => {
    setLoading(true);
    const token = loggedUser?.token || null;
    let { status, data } = await get(THEME_GET, token);
    if (status) {
      // Branding assets live under data.team
      const team = data?.team;
      if (team) {
        setLoggedUser((prev) => ({
          ...prev,
          teams: {
            ...(prev.teams || {}),
            ...team,
          },
        }));
      }
      if (team?.id) setTeamIdFromApi(team.id);
      if (team?.logo) {
        setLogo(team.logo);
        setLogoPreview(
          team.logo.startsWith("http") || team.logo.startsWith("data:")
            ? team.logo
            : baseURL.PUBLIC_URL + team.logo
        );
      }
      if (team?.card_background_image) setCardBackgroundImage(team.card_background_image);
      if (team?.card_color) setCardColor(team.card_color);

      // Dark preset marker: theme_color="#000000" + button_color="#09a0ff" (set by dark useEffect)
      const isDarkPreset =
        data?.theme_color === "#000000" &&
        data?.button_color?.toLowerCase() === "#09a0ff";

      if (data?.theme_color == null) {
        // Light theme — default button color, card branding only
        setSelectedTheme("default");
        if (purchasedPackage.id !== 1) setDefaultTheme(true);
        applyLightTheme();
        applyBrandingColors(null, team?.card_color);
      } else if (isDarkPreset) {
        // Default Branding Dark preset — no team branding override
        setSelectedTheme("dark");
        applyDarkTheme();
      } else {
        // Team Branding Custom theme — restore all saved selections
        const savedButtonColor = data?.button_color || team?.button_color || "#529e83";
        const savedCardColor = team?.card_color || data?.card_color || "";
        setThemeColor(data.theme_color);
        setButtonColor(savedButtonColor);
        if (savedCardColor) setCardColor(savedCardColor);
        setUseCustomColor(true);
        const savedCustomThemeScope = localStorage.getItem("customThemeScope") || "team";
        setCustomThemeScope(savedCustomThemeScope);
        const isTeamMemberCheck = (loggedUser?.connected_teams?.length ?? 0) > 0;
        if (!isTeamMemberCheck) setActiveTab(savedCustomThemeScope);
        applyCSSVariables(data.theme_color);
        applyBrandingColors(savedButtonColor, savedCardColor || null);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchColors();
  }, []);

  const isTeamMember = (loggedUser?.connected_teams?.length ?? 0) > 0;

  const isDarkMode = selectedTheme === "dark" && !useCustomColor;
  const brandingTextColor = isDarkMode
    ? "#ffffff"
    : useCustomColor && themeColor
      ? (() => { try { return chroma(themeColor).luminance() > 0.4 ? "#333333" : "#ffffff"; } catch (_) { return "#333333"; } })()
      : "#333333";
  const brandingHintColor = isDarkMode ? "#aaaaaa" : "#666666";

  const colorPickerLabel = (
    <label
      htmlFor="customThemeColor"
      style={{
        display: "inline-flex", alignItems: "center", gap: 10,
        marginTop: 10, padding: "6px 14px 6px 6px",
        borderRadius: 10, border: "1.5px solid #e0e0e0",
        background: "#fafafa", cursor: "pointer",
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)", width: "fit-content",
      }}
    >
      <span style={{
        display: "inline-block", width: 36, height: 36, borderRadius: 7,
        background: themeColor || "#ffffff", border: "1px solid rgba(0,0,0,0.12)",
        flexShrink: 0, position: "relative", overflow: "hidden",
      }}>
        <input
          type="color"
          id="customThemeColor"
          value={themeColor || "#ffffff"}
          onChange={(e) => setThemeColor(e.target.value)}
          style={{ opacity: 0, position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "pointer", border: "none", padding: 0 }}
        />
      </span>
      <input
        type="text"
        value={themeColor || ""}
        placeholder="#000000"
        maxLength={7}
        onChange={(e) => {
          const val = e.target.value;
          setThemeColor(val);
          if (/^#[0-9A-Fa-f]{6}$/.test(val)) applyCSSVariables(val);
        }}
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600, color: "#333", letterSpacing: 0.5, border: "none", outline: "none", background: "transparent", width: 80 }}
      />
    </label>
  );

  return (
    <>
      {!loading ? (
        <Card className="bioDataWrapper">
          <Card.Body className="themeWrapper">

            {/* ── Tab navigation ── */}
            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
              <Nav.Item>
                <Nav.Link eventKey="default">Default Branding</Nav.Link>
              </Nav.Item>
              {!isTeamMember && (
                <Nav.Item>
                  <Nav.Link eventKey="team">Team Branding</Nav.Link>
                </Nav.Item>
              )}
            </Nav>

            {/* ── DEFAULT BRANDING TAB ── */}
            {activeTab === "default" && (
              <>
                <div className="colorCheckBox">
                  <h4 className="mb-3">Choose Your Theme</h4>
                  <div className="row theme-radio-wrapper">
                    <div className="col-12 col-md-6 col-lg-2">
                      <div className="theme-radio">
                        <Form.Check
                          type="radio"
                          name="defaultTheme"
                          label="Light theme"
                          value="default"
                          checked={selectedTheme === "default" && !useCustomColor}
                          onChange={() => { setSelectedTheme("default"); setUseCustomColor(false); }}
                          disabled={isTeamMember}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-2">
                      <div className="theme-radio">
                        <Form.Check
                          type="radio"
                          name="defaultTheme"
                          label="Dark theme"
                          value="dark"
                          checked={selectedTheme === "dark" && !useCustomColor}
                          onChange={() => { setSelectedTheme("dark"); setUseCustomColor(false); }}
                          disabled={isTeamMember}
                        />
                      </div>
                    </div>
                    {(purchasedPackage?.id !== 1 || isTeamMember) && (
                      <div className="col-12 col-md-6 col-lg-2">
                        <div className="theme-radio">
                          <Form.Check
                            type="radio"
                            name="defaultTheme"
                            label="Custom theme"
                            value="custom"
                            checked={useCustomColor && customThemeScope === "default"}
                            onChange={() => {
                              setUseCustomColor(true);
                              setCustomThemeScope("default");
                              setSelectedTheme("");
                            }}
                            disabled={isTeamMember}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {(purchasedPackage?.id !== 1 || isTeamMember) &&
                  useCustomColor &&
                  customThemeScope === "default" && (
                    <div className="colorBox mt-3">
                      <Form.Group>
                        <label className="formColorLabel">Theme Color</label>
                        <div className="formBox">
                          {Colors.map((color) => (
                            <div
                              key={color.id}
                              className={
                                themeColor?.toLowerCase() === color.bgColor.toLowerCase()
                                  ? "activeColor"
                                  : "inActiveColor"
                              }
                            >
                              <button
                                type="button"
                                className="colorButton"
                                style={{ backgroundColor: color.bgColor }}
                                onClick={() => setThemeColor(color.bgColor)}
                                disabled={isTeamMember}
                                aria-label={`Select theme color ${color.bgColor}`}
                              />
                            </div>
                          ))}
                        </div>
                      </Form.Group>
                    </div>
                  )}

                {isTeamMember ? (
                  <p className="mt-3 mb-0" style={{
                    fontSize: 14, color: "#ffffff",
                    background: "rgba(0,0,0,0.25)", borderRadius: 8,
                    padding: "10px 14px", display: "inline-block"
                  }}>
                    Branding is managed by your team owner and can't be edited here.
                  </p>
                ) : (
                  <Button
                    variant="primary"
                    type="button"
                    className="mt-4"
                    onClick={handleDefaultSubmit}
                  >
                    Submit
                  </Button>
                )}
              </>
            )}

            {/* ── TEAM BRANDING TAB ── */}
            {activeTab === "team" && !isTeamMember && (
              <>
                {/* Custom theme color */}
                {purchasedPackage?.id !== 1 && (
                  <div className="colorCheckBox mb-4">
                    <h4 className="mb-3">Choose Your Theme</h4>
                    <div className="row theme-radio-wrapper">
                      <div className="col-12 col-md-6 col-lg-2">
                        <div className="theme-radio">
                          <Form.Check
                            type="radio"
                            name="teamTheme"
                            label="Custom theme"
                            value="custom"
                            checked={useCustomColor && customThemeScope === "team"}
                            onChange={() => {
                              setUseCustomColor(true);
                              setCustomThemeScope("team");
                              setSelectedTheme("");
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {useCustomColor && customThemeScope === "team" && (
                      <div className="colorBox mt-3">
                        <Form.Group controlId="customThemeColor">
                          <label className="formColorLabel" style={{ color: "var(--primary_text_color)" }}>Theme Color</label>
                          {colorPickerLabel}
                        </Form.Group>
                      </div>
                    )}
                  </div>
                )}

                <hr className="my-4" />

                {/* Branding Assets */}
                <div style={{ color: brandingTextColor }}>
                  <h5 className="mb-3">Branding Assets</h5>

                  {/* Logo */}
                  <div className="mb-4">
                    <label className="formColorLabel mb-2 d-block">Logo</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <div style={{
                        width: 80, height: 80, borderRadius: 12,
                        border: "2px dashed #ccc", background: "#f9f9f9",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        overflow: "hidden", flexShrink: 0,
                      }}>
                        {logoPreview || logo ? (
                          <img
                            src={logoPreview || (logo.startsWith("http") ? logo : baseURL.PUBLIC_URL + logo)}
                            alt="logo"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <FaPlus style={{ color: "#bbb", fontSize: 22 }} />
                        )}
                      </div>
                      <div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => logoFileRef.current?.click()}
                          disabled={logoUploading}
                          style={{ color: brandingTextColor, borderColor: brandingTextColor }}
                        >
                          {logoUploading ? "Uploading…" : "Upload Logo"}
                        </Button>
                        {(logo || logoPreview) && (
                          <Button variant="link" size="sm" className="ms-2" style={{ color: brandingTextColor }} onClick={handleRemoveLogo}>
                            Remove
                          </Button>
                        )}
                        <p className="mt-1 mb-0" style={{ fontSize: 12, color: brandingHintColor, opacity: 0.8 }}>
                          PNG, JPG or SVG recommended
                        </p>
                      </div>
                    </div>
                    <input ref={logoFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoChange} />
                  </div>

                  {/* Card Background Image */}
                  <div className="mb-4">
                    <label className="formColorLabel mb-2 d-block">Card Background Image</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {CardBackGroundImages.map((bg) => (
                        <button
                          key={bg.id}
                          type="button"
                          onClick={() => { setCardBackgroundImage(bg.img); }}
                          style={{
                            width: 72, height: 48, padding: 0, borderRadius: 8,
                            border: cardBackgroundImage === bg.img ? "5px solid rgb(255 255 255)" : "2px solid #ddd",
                            overflow: "hidden", cursor: "pointer", background: "none", outline: "none",
                            boxShadow: cardBackgroundImage === bg.img ? "0 0 0 2px rgba(13,110,253,0.25)" : "none",
                          }}
                        >
                          <img src={baseURL.PUBLIC_URL + bg.img} alt={`Background ${bg.id}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </button>
                      ))}

                      {/* Custom upload thumbnail */}
                      {cardBackgroundImage && !CardBackGroundImages.some((bg) => bg.img === cardBackgroundImage) && (
                        <button type="button" style={{
                          width: 72, height: 48, padding: 0, borderRadius: 8,
                          border: "5px solid rgb(255 255 255)", overflow: "hidden",
                          cursor: "default", background: "none", outline: "none",
                          boxShadow: "0 0 0 2px rgba(13,110,253,0.25)",
                        }}>
                          <img
                            src={cardBackgroundImage.startsWith("http") || cardBackgroundImage.startsWith("data:") ? cardBackgroundImage : baseURL.PUBLIC_URL + cardBackgroundImage}
                            alt="Custom background"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => cardBgFileRef.current?.click()}
                        disabled={cardBgUploading}
                        style={{
                          width: 72, height: 48, borderRadius: 8, border: "2px dashed #aaa",
                          background: "transparent", cursor: "pointer", fontSize: 11, color: brandingTextColor,
                          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
                        }}
                      >
                        {cardBgUploading ? "…" : (<><span style={{ fontSize: 16, lineHeight: 1 }}>+</span><span>Upload</span></>)}
                      </button>
                      <input ref={cardBgFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCardBgChange} />

                      {cardBackgroundImage && (
                        <button
                          type="button"
                          onClick={handleClearCardBg}
                          style={{
                            width: 72, height: 48, borderRadius: 8, border: "1.5px solid #f5c6cb",
                            background: "#fff5f5", cursor: "pointer", fontSize: 11, color: "#d9534f",
                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
                          }}
                        >
                          <span style={{ fontSize: 15, lineHeight: 1 }}>✕</span>
                          <span>Clear</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Card Color */}
                  <div className="mb-4">
                    <label className="formColorLabel mb-2 d-block">Card Color</label>
                    <label htmlFor="cardColorInput" style={{
                      display: "inline-flex", alignItems: "center", gap: 10,
                      marginTop: 6, padding: "6px 14px 6px 6px",
                      borderRadius: 10, border: "1.5px solid #e0e0e0",
                      background: "#fafafa", cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.07)", width: "fit-content",
                    }}>
                      <span style={{
                        display: "inline-block", width: 36, height: 36, borderRadius: 7,
                        background: cardColor || "#ffffff", border: "1px solid rgba(0,0,0,0.12)",
                        flexShrink: 0, position: "relative", overflow: "hidden",
                      }}>
                        <input
                          type="color"
                          id="cardColorInput"
                          value={cardColor || "#ffffff"}
                          onChange={(e) => setCardColor(e.target.value)}
                          style={{ opacity: 0, position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "pointer", border: "none", padding: 0 }}
                        />
                      </span>
                      <input
                        type="text"
                        value={cardColor || ""}
                        placeholder="#000000"
                        maxLength={7}
                        onChange={(e) => setCardColor(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600, color: "#333333", letterSpacing: 0.5, border: "none", outline: "none", background: "transparent", width: 80 }}
                      />
                    </label>
                  </div>

                  {/* Button Color */}
                  <div className="mb-4">
                    <label className="formColorLabel mb-2 d-block">Button Color</label>
                    <label htmlFor="buttonColorInput" style={{
                      display: "inline-flex", alignItems: "center", gap: 10,
                      marginTop: 6, padding: "6px 14px 6px 6px",
                      borderRadius: 10, border: "1.5px solid #e0e0e0",
                      background: "#fafafa", cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.07)", width: "fit-content",
                    }}>
                      <span style={{
                        display: "inline-block", width: 36, height: 36, borderRadius: 7,
                        background: buttonColor || "#ffffff", border: "1px solid rgba(0,0,0,0.12)",
                        flexShrink: 0, position: "relative", overflow: "hidden",
                      }}>
                        <input
                          type="color"
                          id="buttonColorInput"
                          value={buttonColor || "#ffffff"}
                          onChange={(e) => setButtonColor(e.target.value)}
                          style={{ opacity: 0, position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "pointer", border: "none", padding: 0 }}
                        />
                      </span>
                      <input
                        type="text"
                        value={buttonColor || ""}
                        placeholder="#000000"
                        maxLength={7}
                        onChange={(e) => setButtonColor(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600, color: "#333333", letterSpacing: 0.5, border: "none", outline: "none", background: "transparent", width: 80 }}
                      />
                    </label>
                  </div>
                </div>

                <Button
                  variant="primary"
                  type="button"
                  className="mt-3"
                  onClick={handleTeamBrandingSubmit}
                  style={useCustomColor && themeColor ? (() => {
                    try {
                      const isLight = chroma(themeColor).luminance() > 0.4;
                      const accentColor = isLight ? chroma(themeColor).darken(1.5).hex() : chroma(themeColor).brighten(1.5).hex();
                      return { color: chroma(accentColor).luminance() > 0.4 ? "#333333" : "#ffffff" };
                    } catch (_) { return {}; }
                  })() : {}}
                >
                  Submit
                </Button>
              </>
            )}

          </Card.Body>
        </Card>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default Theme;
